import Box from 'components/shared/Box'
import Button from 'components/shared/Button'
import Price from 'components/shared/Price'
import Section from 'components/shared/Section'
import Text from 'components/shared/Text'
import { FormattedMessage } from 'react-intl'
import SeparatedList from 'components/shared/SeparatedList'
import {
    AddShippingRequestDto,
    OrderBillingDataForCompanyDto,
    OrderBillingDataForUserDto,
    useAddShippingToCartPutMutation,
    useCreateOrderMutation,
} from 'store/api-slice/hooks'
import { calculateVouchersTypeEvent, pushDataLayer } from 'helpers/helpers'
import { useApp } from 'store/steps'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { FieldError, setErrors } from 'store/order-slice'
import { useErrorMessages } from 'utils/hooks/useErrorMessages'
import { VOUCHER_DELIVERY_PRICE } from 'constants/prices'
import { setIsErrorModalOpened } from 'store/general-slice'
import { scrollTo } from 'helpers/animateScroll'
import { useMemo, useState } from 'react'
import { handleError } from 'helpers/errors'
import PaymentExpiryInfo from 'routes/OrderAndPayment/sections/PlaceOrderSection/components/PaymentExpiryInfo'
import { useCart } from 'store/cart-slice/hooks'
import Loader from 'components/shared/Loader'
import { useVouchersContent } from 'utils/hooks/useVouchersContent'

const VouchersPlaceOrderSection = () => {
    const dispatch = useAppDispatch()
    const { language } = useApp()
    const { setErrorMessages } = useErrorMessages()
    const { isCartFetching } = useCart()

    const {
        contact,
        invoice,
        personalInvoice,
        isAgreementChecked,
        optionalAgreements,
        shipping,
    } = useAppSelector((state) => state.order)
    const { cart, items } = useAppSelector((state) => state.cart)

    const { deliveryType } = shipping
    const { cartId, totalPrice, payByCardPrice, giftCards } = cart || {}
    const { vouchers } = items

    const [placeOrder, createOrderStatus] = useCreateOrderMutation()
    const [postShippingMethod, postShippingMethodStatus] =
        useAddShippingToCartPutMutation()

    const [savedUserId, setSavedUserId] = useState<string | undefined>(
        undefined
    )

    const orderSumPrice = (totalPrice ?? 0) - (payByCardPrice ?? 0)
    const isShippingInCart = items.shipping?.productType === 'shipping'

    const isOnlinePaymentRequired = !!totalPrice && orderSumPrice > 0
    const isAnyPromotionVoucher = useMemo(() => {
        if (items && items.vouchers.length) {
            const isPromotionVoucherInCart = items.vouchers.some(
                (voucher) => voucher.detail.voucherType === 'amount'
            )
            return isPromotionVoucherInCart
        }
        return false
    }, [cart])

    const isPlaceOrderProcessing =
        createOrderStatus.isLoading || postShippingMethodStatus.isLoading

    const { confirmEmail, email, name, surname, phone } = contact
    const { street, apartment, city, companyName, nip, postCode } = invoice
    const {
        apartment: personalInvoiceAppartment,
        city: personalInvoiceCity,
        postCode: personalInvoicePostCode,
        street: personalInvoiceStreet,
    } = personalInvoice

    const shippingData: AddShippingRequestDto =
        deliveryType === 'shipping'
            ? {
                  deliveryType,
                  shippingMethodCode: 'inpost_delivery_address',
                  name: shipping.name,
                  surname: shipping.surname,
                  phone: shipping.phone,
                  email: null,
                  street: shipping.street,
                  apartment: shipping.apartment.trim() || null,
                  postCode: shipping.postCode,
                  city: shipping.city,
              }
            : {
                  deliveryType,
                  shippingMethodCode: 'email',
                  email: shipping.email,
                  name: null,
                  surname: null,
                  street: null,
                  postCode: null,
                  city: null,
                  phone: null,
              }

    // < analytics
    const vouchersTypeEvent = useMemo(
        () => calculateVouchersTypeEvent(vouchers),
        [vouchers]
    )
    // analytics >

    const handlePostPlaceOrder = (userId?: string) => {
        if (!cartId) return

        let orderBillingData:
            | OrderBillingDataForCompanyDto
            | OrderBillingDataForUserDto
            | undefined = undefined

        if (invoice.isChecked) {
            orderBillingData = {
                city,
                companyName,
                nip,
                postCode,
                street,
                apartment,
            }
        }

        if (
            personalInvoice.isChecked &&
            (personalInvoiceCity !== '' ||
                personalInvoicePostCode !== '' ||
                personalInvoiceStreet !== '' ||
                personalInvoiceAppartment !== '')
        ) {
            orderBillingData = {
                city: personalInvoiceCity,
                postCode: personalInvoicePostCode,
                street: personalInvoiceStreet,
                apartment: personalInvoiceAppartment,
            }
        }

        // < analytics
        const passedUserId = userId || savedUserId
        if (userId) setSavedUserId(userId)
        // analytics >

        postShippingMethod({
            _locale: language,
            addShippingToCartCommand: {
                cartId,
                shippingData,
            },
        })
            .then((data) => handleError(data))
            .then(() =>
                placeOrder({
                    createOrderCommand: {
                        email,
                        name,
                        surname,
                        confirmEmail,
                        phone: phone.trim() || null,
                        optionalAgreements,
                        orderBillingData,
                        hasRequiredAgreementsAccepted: isAgreementChecked,
                        userId: passedUserId,
                    },
                    cartCreatorType: 'voucher',
                    _locale: language,
                })
            )
            .then((data) => {
                handleError(data)

                if ('data' in data) {
                    const { link } = data.data
                    if (!link) return

                    // < analytics
                    pushDataLayer(vouchersTypeEvent, {
                        event: 'funnelStep',
                        event_category: 'vouchers',
                        event_action: 'checkout_form_sent',
                        event_label: '',
                    })
                    // analytics >

                    window.location.href = link
                }
            })
            .catch((error) => {
                setErrorMessages(error)

                if (!error.messages) {
                    dispatch(setIsErrorModalOpened(true))
                }
            })
    }

    const handlePlaceOrder = () => {
        dispatch(setErrors([]))

        if (!cartId) return
        if (!isAgreementChecked) {
            const message: FieldError[] = [
                {
                    property_path: 'isRequitedTermsAccepted',
                    message: 'This value should not be blank.',
                },
            ]

            scrollTo({
                id: 'agreementsBox',
                duration: 1000,
                margin: 100,
            })
            return dispatch(setErrors(message))
        }
        handlePostPlaceOrder()
    }
    const vouchersContent = useVouchersContent({
        deliveryPrice: VOUCHER_DELIVERY_PRICE,
    })

    return (
        <Section hasSmallerSpacingsY>
            {!isAnyPromotionVoucher && (
                <Box mb={16}>{vouchersContent?.vouchers}</Box>
            )}
            <SeparatedList
                gap={
                    (giftCards && giftCards.length) || isShippingInCart ? 16 : 0
                }
                borderWidth={2}
                direction="column"
                hasLastItemBorder={false}
                turnOffChildTopBorder={
                    (giftCards && giftCards.length) || isShippingInCart
                        ? undefined
                        : [2, 3]
                }
            >
                {vouchersContent?.summary}
                <Box
                    direction="row"
                    justify="space-between"
                    align="flex-end"
                    mb={16}
                >
                    <Text variant="body" color="grayscale_900">
                        <FormattedMessage
                            id="global.price.label_total_summary"
                            defaultMessage="Do zapłaty"
                        />
                        :
                    </Text>
                    {isCartFetching ? (
                        <Loader />
                    ) : (
                        <Price value={orderSumPrice || 0} size="heading2" />
                    )}
                </Box>
                <Box align="flex-end" mt={16}>
                    <Button
                        onClick={handlePlaceOrder}
                        isLoading={isPlaceOrderProcessing}
                        testId="payment-button-pay"
                    >
                        <FormattedMessage
                            id="order.discount.btn_payment_caption"
                            defaultMessage="Kupuję i płacę"
                        />
                    </Button>
                    {isOnlinePaymentRequired && <PaymentExpiryInfo />}
                </Box>
            </SeparatedList>
        </Section>
    )
}

export default VouchersPlaceOrderSection
